import { useQueryClient } from '@tanstack/react-query'
import { AUDIT_LOG_KEYS } from './audit-logs'
import { api } from './client'
import type { paths } from './schema'

// The Product catalog (UI-SCREENS P-01 / P-02 / P-03, #99). Everyone sees Products; only the
// Owner creates, edits and archives them — the API answers Staff with 403.
export type Product = paths['/api/w/{slug}/products']['get']['responses'][200]['content']['application/json'][number]
// Informational only (#98 Q1): a badge and a list filter.
export type ProductStatus = 'in_development' | 'active'
export const PRODUCT_STATUSES: ProductStatus[] = ['in_development', 'active']

const PRODUCTS = ['get', '/api/w/{slug}/products'] as const
const PRODUCT = ['get', '/api/w/{slug}/products/{id}'] as const

export function useProducts(slug: string, { archived = false }: { archived?: boolean } = {}) {
  return api.useQuery(
    'get',
    '/api/w/{slug}/products',
    { params: { path: { slug }, query: archived ? { archived: true } : {} } },
    { retry: false },
  )
}

export function useProduct(slug: string, id: number, { enabled = true }: { enabled?: boolean } = {}) {
  return api.useQuery('get', '/api/w/{slug}/products/{id}', { params: { path: { slug, id } } }, { retry: false, enabled })
}

// Yield % and top failure reasons per Model (P-03, 04j #136). Read only; it is refetched whenever
// P-03 mounts, so a Batch completed or voided meanwhile shows up.
export type ModelYield =
  paths['/api/w/{slug}/products/{product_id}/yield']['get']['responses'][200]['content']['application/json']['models'][number]

export function useProductYield(slug: string, productId: number) {
  return api.useQuery(
    'get',
    '/api/w/{slug}/products/{product_id}/yield',
    { params: { path: { slug, product_id: productId } } },
    { retry: false },
  )
}

// After any change (or a failure, which may mean the data changed meanwhile) the Product and the
// list are refetched.
function useRefetch() {
  const queryClient = useQueryClient()
  return async () => {
    await Promise.all(
      [PRODUCTS, PRODUCT, AUDIT_LOG_KEYS.products].map((queryKey) => queryClient.invalidateQueries({ queryKey: [...queryKey] })),
    )
  }
}

export function useCreateProduct() {
  const onSettled = useRefetch()
  return api.useMutation('post', '/api/w/{slug}/products', { onSettled })
}

export function useUpdateProduct() {
  const onSettled = useRefetch()
  return api.useMutation('patch', '/api/w/{slug}/products/{id}', { onSettled })
}

export function useArchiveProduct() {
  const onSettled = useRefetch()
  return api.useMutation('post', '/api/w/{slug}/products/{product_id}/archive', { onSettled })
}

export function useUnarchiveProduct() {
  const onSettled = useRefetch()
  return api.useMutation('delete', '/api/w/{slug}/products/{product_id}/archive', { onSettled })
}
